import * as Sentry from "@sentry/react";
import update from "immutability-helper";
import type { Organization } from "@theirstory/common/api_types/backend";

import {
  AUTH_2FA_USER,
  AUTH_USER,
  CONTACT_USER,
  INIT_VISITOR_ID,
  NO_USER_AUTH,
  ONE_ORGANIZATION_LOADED,
  SWITCH_ORG,
  UNAUTH_USER,
  UPDATE_AVIARY_TOKENS,
  USER_ORG_REQUIRES_SESSION_PWD,
} from "../actions/types";

export interface AvpOrganization {
  id: number;
  subdomain: string;
  name: string;
}

export interface ManagedOrg {
  _id: string;
  organization_name: string;
  organizational_code: string;
}

// Alias — member_orgs rows have the same shape as managed_orgs
export type MemberOrg = ManagedOrg;

export enum Role {
  ADMIN = "admin",
  MANAGER = "manager",
  ORGMANAGER = "org_manager",
  USER = "user",
  GUEST = "guest",
}

export type AuthState = {
  authenticationPending: boolean;
  authenticated?: boolean;
  is2FA: boolean;
  is2FASuccess?: boolean;
  is2FACompleted?: boolean;
  orgLevel2faEnabled: boolean;
  twofactorAuthenticationEnabled: boolean;
  forceTwofactorAuthentication: boolean;
  token?: string;
  superToken?: string;
  full_name?: string;
  user_id?: string;
  email?: string;
  role?: Role;
  organization_info?: Organization;
  managed_orgs?: ManagedOrg[];
  member_orgs?: MemberOrg[];
  /** From auth API; true for platform admin or org manager of the active org. */
  can_manage_active_organization?: boolean;
  aviary_client?: string;
  aviary_token?: string;
  aviary_uid?: string;
  visitorId?: string;
  aviary_user_details?: { organizations: AvpOrganization[] };
  api_version?: string;

  // There are only used below in CONTACT_USER action. Not sure if they
  // are actually needed here for but for time being adding to avoid
  // TS errors.
  contact_email?: string;
  contact_full_name?: string;
  message?: string;
  create_gallery?: boolean;
};

// This is a subset of full auth state that represents just
// the attributes that auth API returns for a logged in
// user. This list represents the attributes that we MUST
// reset whenever the user logs out
const initialUserAuthProps = {
  token: null,
  full_name: null,
  user_id: null,
  email: null,
  role: null,
  organization_info: null,
  aviary_client: null,
  aviary_token: null,
  aviary_uid: null,
  aviary_user_details: null,
};

const initialState: AuthState = {
  ...initialUserAuthProps,
  authenticated: false,
  is2FA: false,
  is2FASuccess: false,
  is2FACompleted: false,
  authenticationPending: true,
  superToken: null,
  api_version: null,
  orgLevel2faEnabled: false,
  twofactorAuthenticationEnabled: false,
  forceTwofactorAuthentication: false,
};

// authentication reducer
const authReducer = (state = initialState, action): AuthState => {
  switch (action.type) {
    case INIT_VISITOR_ID:
      return update(state, { visitorId: { $set: action.payload } });
    case NO_USER_AUTH: {
      return update(state, {
        authenticationPending: { $set: false },
        authenticated: { $set: false },
      });
    }

    case UPDATE_AVIARY_TOKENS: {
      return update(state, {
        aviary_client: { $set: action.payload.aviary_client },
        aviary_token: { $set: action.payload.aviary_token },
        aviary_uid: { $set: action.payload.aviary_uid },
        aviary_user_details: { $set: action.payload.aviary_user_details },
      });
    }

    case AUTH_2FA_USER: {
      const { userAuthInfo, isUserSwitch } = action.payload;
      Sentry.setUser({ ...userAuthInfo, id: userAuthInfo.user_id });
      const { is2FA, is2FACompleted } = userAuthInfo;
      if (is2FA && is2FACompleted) {
        localStorage.setItem("is2FACompleted", "true");

        return update(state, {
          authenticated: { $set: true },
          authenticationPending: { $set: false },
          superToken: { $set: isUserSwitch ? state.token : null },
          $merge: { ...initialUserAuthProps, ...userAuthInfo },
        });
      }

      return update(state, {
        authenticated: { $set: userAuthInfo.is2FACompleted },
        authenticationPending: { $set: false },
        superToken: { $set: isUserSwitch ? state.token : null },
        $merge: { ...initialUserAuthProps, ...userAuthInfo },
      });
    }

    // set state to authenticated
    case AUTH_USER: {
      const { userAuthInfo, isUserSwitch } = action.payload;
      Sentry.setUser({ ...userAuthInfo, id: userAuthInfo.user_id });
      return update(state, {
        authenticated: { $set: true },
        authenticationPending: { $set: false },
        superToken: { $set: isUserSwitch ? state.token : null },
        $merge: { ...initialUserAuthProps, ...userAuthInfo },
      });
    }
    case CONTACT_USER: {
      const { contact_email: contactEmail, contact_full_name: contactFullName, message } = action.payload;
      return update(state, {
        contact_email: { $set: contactEmail },
        contact_full_name: { $set: contactFullName },
        message: { $set: message },
      });
    }
    // set state to unauthenticated
    case UNAUTH_USER: {
      Sentry.setUser(null);
      return update(state, {
        authenticated: { $set: false },
        $merge: initialUserAuthProps,
      });
    }

    case USER_ORG_REQUIRES_SESSION_PWD: {
      return update(state, {
        organization_info: {
          require_session_passwords: { $set: true },
        },
      });
    }

    case ONE_ORGANIZATION_LOADED: {
      if (
        state.organization_info != null &&
        state.organization_info.organizational_code === action.payload.organization.organizational_code
      ) {
        return update(state, {
          organization_info: {
            $set: action.payload.organization,
          },
        });
      } else {
        return state;
      }
    }

    case SWITCH_ORG: {
      return update(state, {
        organization_info: { $set: action.payload.organization_info },
        managed_orgs: { $set: action.payload.managed_orgs },
        member_orgs: { $set: action.payload.member_orgs },
        can_manage_active_organization: { $set: action.payload.can_manage_active_organization },
      });
    }

    default:
      return state;
  }
};

export default authReducer;
